import type { RtuRecordState } from '../../types/RTU';
import { fleetCounts, fleetRows, fleetTotal } from './rtuCluster';

type CurrentFleetRow = { id?: number; assetId?: number | null; recordState?: RtuRecordState | null };

export interface CurrentFleet<T> {
  /** Rows that represent the fleet's devices and unverified records (displayed rows minus standalone former records). */
  rows: T[];
  /** Physical devices with a CURRENT (confirmed) identity. */
  confirmed: number;
  /** UNRESOLVED records: identity is ambiguous, not verified hardware. */
  unresolved: number;
  /** Legacy records with no usable identity (null recordState): not verified hardware. */
  unidentified: number;
  /** confirmed + unresolved + unidentified. Equals rows.length when each asset has one CURRENT row. */
  total: number;
}

/**
 * Single source of the current-fleet numbers used by every dashboard card and the map total.
 * Built only from the rtuCluster helpers: each physical device counts once, former records are not devices,
 * and the unverified part (unresolved + unidentified) is always reported separately from confirmed devices.
 */
export const currentFleet = <T extends CurrentFleetRow>(rows: T[]): CurrentFleet<T> => {
  const counts = fleetCounts(rows);
  return {
    rows: fleetRows(rows),
    confirmed: counts.physical,
    unresolved: counts.unresolved,
    unidentified: counts.unidentified,
    total: fleetTotal(counts),
  };
};
